import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileBarChart, FileDown, FileSpreadsheet, TimerReset } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Reportes() {
  const [container, setContainer] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const params = { container, date_from: dateFrom, date_to: dateTo };

  const { data, refetch, isFetching } = useQuery({
    queryKey: ["reporte-transito", container, dateFrom, dateTo],
    queryFn: async () => (await api.get("/reports/transito", { params })).data,
  });
  const rows = data?.rows || [];

  const exportFile = async (fmt) => {
    const res = await api.get(`/reports/transito/${fmt}`, { params, responseType: "blob" });
    downloadBlob(res.data, `reporte_transito.${fmt === "excel" ? "xlsx" : "pdf"}`);
  };

  const fmtDur = (h) => {
    if (h == null) return "-";
    if (h < 48) return `${h} h`;
    return `${Math.floor(h / 24)} d ${Math.round(h % 24)} h`;
  };

  return (
    <div className="space-y-5" data-testid="reportes-page">
      <div>
        <h1 className="font-outfit text-2xl md:text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <FileBarChart className="w-6 h-6 text-cyan-600" /> Reportes de Tránsito
        </h1>
        <p className="text-sm text-slate-500">Tiempo de permanencia en sitio desde la entrada hasta la salida de cada contenedor</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5 flex-1 min-w-40">
            <Label>Contenedor</Label>
            <Input value={container} onChange={(e) => setContainer(e.target.value.toUpperCase())} placeholder="MSCU..." className="font-mono uppercase" data-testid="report-container-filter" />
          </div>
          <div className="space-y-1.5">
            <Label>Desde</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
          </div>
          <div className="space-y-1.5">
            <Label>Hasta</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
          </div>
          <Button onClick={() => refetch()} variant="outline" className="border-navy text-navy" data-testid="report-apply-button">
            {isFetching ? "Cargando..." : "Aplicar filtros"}
          </Button>
          <Button onClick={() => exportFile("pdf")} className="bg-navy hover:bg-navy-800 text-white" data-testid="export-pdf-button">
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button onClick={() => exportFile("excel")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="export-excel-button">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="report-results-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-lg flex items-center gap-2">
            <TimerReset className="w-4 h-4 text-cyan-600" /> Tránsito por Contenedor
            <Badge variant="secondary" data-testid="report-count">{rows.length} registros</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-navy hover:bg-navy">
                <TableHead className="text-white">Contenedor</TableHead>
                <TableHead className="text-white">Entrada</TableHead>
                <TableHead className="text-white">Salida</TableHead>
                <TableHead className="text-white">Duración en sitio</TableHead>
                <TableHead className="text-white">Estado</TableHead>
                <TableHead className="text-white">Camión</TableHead>
                <TableHead className="text-white">Conductor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10" data-testid="report-empty">Sin datos de tránsito</TableCell></TableRow>
              )}
              {rows.map((r, i) => (
                <TableRow key={`${r.container_number}-${r.entrada_id}-${i}`} data-testid={`report-row-${r.container_number}-${i}`}>
                  <TableCell className="font-mono font-bold tracking-wider">{r.container_number}</TableCell>
                  <TableCell className="text-xs">{r.entrada_id}<br /><span className="text-slate-400">{r.entrada_fecha}</span></TableCell>
                  <TableCell className="text-xs">{r.salida_id}<br /><span className="text-slate-400">{r.salida_fecha}</span></TableCell>
                  <TableCell className="font-mono font-semibold text-navy">{fmtDur(r.duracion_horas)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={r.estado === "En sitio" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}>
                      {r.estado}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.camion}</TableCell>
                  <TableCell className="text-xs">{r.conductor}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
